import { defineStore } from 'pinia'
import { CustomerAccount } from '@team-milva/lab-models/customerAccount'
import type {
  ICustomerAccount,
  ICustomerAccountAgreementFile,
} from '@team-milva/lab-models/customerAccount'
import type { IImageBase } from '@team-milva/lab-models/image'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { managementApi } from '@/globals/typescript/utils/api/managementClient'
import { useAccountStore } from '@/stores/accountStore'
import {
  CustomerAccountPrice,
  type ICustomerAccountPrice,
  type ICustomerAccountPriceAnalysisEntry,
  type ICustomerAccountPriceTatEntry,
  type ICustomerAccountPriceAddonEntry,
  type TCustomerAccountPriceDealEntry,
} from '@team-milva/lab-models/customerAccount'
import type { TLabManagementCurrencyCode } from '@team-milva/lab-models/currency'
import type { TLabCustomerLocale } from '@team-milva/lab-models/language'
import { resolveIsInternal } from '@team-milva/lab-models'
import { withInternalSuffix } from '@/globals/typescript/utils/internalLabel'

type SaveCustomerAccountPriceParams = {
  pricingSegmentID: string | null
  analyses: ICustomerAccountPriceAnalysisEntry[]
  tats: ICustomerAccountPriceTatEntry[]
  addons: ICustomerAccountPriceAddonEntry[]
  deals: TCustomerAccountPriceDealEntry[]
  currencyCode: TLabManagementCurrencyCode | null
}

type CreateCustomerAccountParams = {
  name: string
  currencyCode: TLabManagementCurrencyCode
  pricingSegmentID: string
}

type UpdateCustomerAccountParams = {
  name?: string
  activeState?: 'active' | 'inactive'
  supportedLocales?: TLabCustomerLocale[]
  defaultLocale?: TLabCustomerLocale
}

type UpdateCustomerAccountGeneralInfoParams = {
  name?: string
  companyDetails?: Partial<ICustomerAccount['companyDetails']>
  primaryContactUserID?: string | null
  images?: { logo: IImageBase | null }
  files?: { agreements: ICustomerAccountAgreementFile[] }
  invoicing?: ICustomerAccount['invoicing']
  supportedLocales?: TLabCustomerLocale[]
  defaultLocale?: TLabCustomerLocale
  options?: Partial<ICustomerAccount['options']>
}

export const useCustomerAccountStore = defineStore('customerAccount', {
  state: () => ({
    allCustomerAccounts: [] as CustomerAccount[],
    unsubscribeAllCustomerAccounts: null as (() => void) | null,
    allCustomerAccountPrices: [] as CustomerAccountPrice[],
    listStateFilter: 'active' as 'active' | 'inactive',
    searchText: '' as string,
    unsubscribeAllCustomerAccountPrices: null as (() => void) | null,
  }),

  getters: {
    /**
     * Customer account names as they should be *shown* — an internal account carries a
     * " (intern)" suffix (MIL-252). Keyed by account ID.
     *
     * A derived value, never the stored one: `customerAccount.name` stays the customer's
     * actual name, because it is bound to the General tab's name input (which writes it
     * straight back to Firestore) and to the Business Central customer mapping. Putting
     * the suffix on the stored field would save it into the customer's real name and
     * send it on to BC.
     *
     * **Not for an order's own number.** That label must come from the order's own
     * `options.isInternal` snapshot — an order created while the account was internal
     * keeps its marker even after the account is switched back, which is the whole point
     * of the snapshot. The *customer* column in an order table, by contrast, describes the
     * customer and so follows the account live, exactly as this getter does; both go
     * through `getCustomerCell()` / `getOrderIdentifierCell()` rather than reading here,
     * because those rows are built from plain row objects rather than store state.
     */
    customerAccountDisplayNamesByID: (state): Record<string, string> =>
      Object.fromEntries(
        state.allCustomerAccounts.map((customerAccount) => [
          customerAccount.id,
          withInternalSuffix(
            customerAccount.name,
            resolveIsInternal(customerAccount.options)
          ),
        ])
      ),

    /**
     * Whether new users on this account get a welcome mail (MIL-397). `?? false` because the field
     * is absent on every account created before the opt-in existed — the normal case, not an edge.
     */
    isWelcomeEmailEnabledFor:
      (state) =>
      (customerAccountID: string): boolean =>
        state.allCustomerAccounts.find((x) => x.id === customerAccountID)
          ?.options.sendWelcomeEmails ?? false,

    allCustomerAccountsLoaded: (state): boolean =>
      !!state.unsubscribeAllCustomerAccounts,
    allCustomerAccountPricesLoaded: (state): boolean =>
      !!state.unsubscribeAllCustomerAccountPrices,
  },

  actions: {
    setSearchText(text: string): void {
      this.searchText = text
    },

    getCustomerAccounts(): Promise<CustomerAccount[]> {
      if (this.allCustomerAccountsLoaded)
        return Promise.resolve(this.allCustomerAccounts)

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'CustomerAccounts'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allCustomerAccounts = querySnapshot.docs.map(
            (doc) => new CustomerAccount(doc.data() as ICustomerAccount)
          )
          if (!resolved) {
            resolved = true
            resolve(this.allCustomerAccounts)
          }
        })

        this.unsubscribeAllCustomerAccounts = unsubscribe
      })
    },

    async createCustomerAccount(
      params: CreateCustomerAccountParams
    ): Promise<string> {
      const accountStore = useAccountStore()

      const result = await managementApi(
        'post',
        'lab-accounts/{labAccountID}/customer-accounts',
        {
          path: { labAccountID: accountStore.labAccount?.id ?? '' },
          body: {
            name: params.name,
            // TODO: remove cast after deploying and re-running openapi:gen
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            currencyCode: params.currencyCode as any,
            pricingSegmentID: params.pricingSegmentID,
          },
        }
      )
      if (!result.success) throw new Error(result.code)
      return result.data.id
    },

    async updateCustomerAccount(
      id: string,
      params: UpdateCustomerAccountParams
    ): Promise<void> {
      const accountStore = useAccountStore()

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            customerAccountID: id,
          },
          body: params,
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateCustomerAccountGeneralInfo(
      id: string,
      params: UpdateCustomerAccountGeneralInfoParams
    ): Promise<void> {
      const accountStore = useAccountStore()

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            customerAccountID: id,
          },
          body: {
            ...(params.name !== undefined && { name: params.name }),
            ...(params.companyDetails !== undefined && {
              companyDetails: params.companyDetails,
            }),
            ...(params.primaryContactUserID !== undefined && {
              primaryContactUserID: params.primaryContactUserID,
            }),
            ...(params.images !== undefined && { images: params.images }),
            ...(params.files !== undefined && { files: params.files }),
            ...(params.invoicing !== undefined && {
              invoicing: params.invoicing,
            }),
            ...(params.supportedLocales !== undefined && {
              supportedLocales: params.supportedLocales,
            }),
            ...(params.defaultLocale !== undefined && {
              defaultLocale: params.defaultLocale,
            }),
            ...(params.options !== undefined && { options: params.options }),
          },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    async updateCustomerAccountInsuranceCustomerAccounts(
      id: string,
      insuranceCustomerAccountIDs: string[]
    ): Promise<void> {
      const accountStore = useAccountStore()

      const result = await managementApi(
        'patch',
        'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}/insurance-customer-accounts',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            customerAccountID: id,
          },
          body: { insuranceCustomerAccountIDs },
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    getCustomerAccountPrices(): Promise<CustomerAccountPrice[]> {
      if (this.allCustomerAccountPricesLoaded)
        return Promise.resolve(this.allCustomerAccountPrices)

      return new Promise((resolve) => {
        let resolved = false

        const accountStore = useAccountStore()

        const q = query(
          collection(db, 'Apps', '-lab-management-', 'CustomerAccountPrices'),
          where('labAccountID', '==', accountStore.labAccount?.id)
        )

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
          this.allCustomerAccountPrices = querySnapshot.docs.map(
            (doc) =>
              new CustomerAccountPrice(doc.data() as ICustomerAccountPrice)
          )
          if (!resolved) {
            resolved = true
            resolve(this.allCustomerAccountPrices)
          }
        })

        this.unsubscribeAllCustomerAccountPrices = unsubscribe
      })
    },

    async saveCustomerAccountPrice(
      customerAccountID: string,
      params: SaveCustomerAccountPriceParams
    ): Promise<void> {
      const accountStore = useAccountStore()

      // TODO: remove cast after deploying and re-running openapi:gen —
      // `addons` isn't in the deployed OpenAPI spec's body type yet.
      const body = {
        pricingSegmentID: params.pricingSegmentID,
        currencyCode: params.currencyCode,
        analyses: params.analyses,
        tats: params.tats,
        addons: params.addons,
        deals: params.deals,
      }

      const result = await managementApi(
        'put',
        'lab-accounts/{labAccountID}/customer-accounts/{customerAccountID}/pricing',
        {
          path: {
            labAccountID: accountStore.labAccount?.id ?? '',
            customerAccountID,
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          body: body as any,
        }
      )
      if (!result.success) throw new Error(result.code)
    },

    // Hydrates from a Display's public data endpoint — see orderStore.ts.
    hydrateFromPublicData(raw: unknown[]): void {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.allCustomerAccounts = (raw as any[]).map((x) => new CustomerAccount(x))
    },

    resetStore(): void {
      this.unsubscribeAllCustomerAccounts?.()
      this.allCustomerAccounts = []
      this.unsubscribeAllCustomerAccounts = null
      this.unsubscribeAllCustomerAccountPrices?.()
      this.allCustomerAccountPrices = []
      this.unsubscribeAllCustomerAccountPrices = null
      this.listStateFilter = 'active'
      this.searchText = ''
    },
  },
})

